/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useRef } from 'react';
import MultiSelectPopup from 'components/common/MultiSelectPopup';
import Utils from 'utils/Utils';

interface ChallengeRatingSelectorProps {
  challenge_rating: string;
  onSave: (value: string) => void;
  isOpen: boolean;
  useMm2024: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const ChallengeRatingSelector: React.FC<ChallengeRatingSelectorProps> = ({ challenge_rating, onSave, isOpen, useMm2024, setIsOpen }) => {
  const crs: string[] = ['0', '1/8', '1/4', '1/2'];
  for (let i = 1; i <= 30; i++) {
    crs.push('' + i);
  }

  const renderCr = (cr: string): string => {
    const xp: number = Utils.getXpByCR(cr);
    const xpStr = new Intl.NumberFormat('en-US').format(xp);
    if (useMm2024) {
      const pb: number = Utils.getProfBonusByCR(cr);
      return `${cr}\u00A0(XP\u00A0${xpStr};\u00A0PB\u00A0${pb})`;
    }
    return `${cr}\u00A0(${xpStr}\u00A0XP)`;
  };

  const options: string[] = crs.map(cr => renderCr(cr));

  const handleChange = (selectedValues: string[]) => {
    onSave(selectedValues[0].split(/\s/)[0]);
  };

  const valueTextRef = useRef<HTMLSpanElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (valueTextRef.current && !valueTextRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  // Check if content is empty
  if (!challenge_rating && !isOpen) {
    return null;
  }

  return (
    <span className="multi-select-container edit-field challenge-rating">
      {isOpen && (
        <MultiSelectPopup
          columns={3}
          values={renderCr(challenge_rating)}
          singleSelection={true}
          options={options}
          onChange={handleChange}
          onClickOutside={handleClickOutside}
        />
      )}
      <span onMouseDown={() => setIsOpen(true)} ref={valueTextRef}>
        {renderCr(challenge_rating)}
      </span>
    </span>
  );
};

export default ChallengeRatingSelector;
